import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, toHandlers as _toHandlers, mergeProps as _mergeProps, withCtx as _withCtx, toDisplayString as _toDisplayString, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "flex flex-col md:flex-row justify-between gap-4 text-sm text-gray-600 mb-4 px-2 mt-4" }
const _hoisted_2 = /*#__PURE__*/_createStaticVNode("<div class=\"flex flex-wrap gap-4\"><div class=\"flex items-center gap-2\"><span class=\"w-2.5 h-3.5 rounded-full border border-green-500 bg-green-100 inline-block\"></span> Vinculada </div><div class=\"flex items-center gap-2\"><span class=\"w-2.5 h-3.5 rounded-full border border-yellow-400 bg-yellow-100 inline-block\"></span> Em edição </div><div class=\"flex items-center gap-2\"><span class=\"w-2.5 h-3.5 rounded-full border border-red-500 bg-red-100 inline-block\"></span> Não vinculada </div></div>", 1)
const _hoisted_3 = { class: "flex flex-col sm:flex-row items-start sm:items-center gap-2 relative" }
const _hoisted_4 = {
  key: 1,
  class: "fixed top-[calc(25vh+4rem)] right-[25vw] z-[50] bg-white border border-gray-200 rounded-lg shadow-md p-4 w-[320px]"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("h3", { class: "text-base font-semibold text-gray-800" }, "Adicionar política", -1)
const _hoisted_6 = {
  key: 2,
  class: "fixed top-[calc(25vh+4rem)] right-[25vw] z-50 bg-white border border-gray-200 rounded-lg shadow-md p-4 w-[320px]"
}
const _hoisted_7 = /*#__PURE__*/_createElementVNode("h3", { class: "text-base font-semibold text-gray-800" }, "Editar política", -1)
const _hoisted_8 = { class: "text-xs text-gray-500 font-medium mb-3" }
const _hoisted_9 = { class: "flex gap-2" }

import { ref, computed, watch, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { mdiCalendar, mdiPlus } from '@mdi/js'
import { DatePicker } from 'v-calendar'

import PolicyService from '@/Network/Services/DsPolicyService'
import type {
  CompanyDomainItem,
  GetCompanyDomainResponse,
  GetHealthUnitDomainResponse,
  GetPositionDomainResponse,
  GetScheduleDomainResponse
} from '@/Network/Types/Responses/DsPolicyResponses'
import TableModular from '@/components/designSystem/Table/TableModular/TableModular.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import EmptyList from '@/components/EmptyList.vue'
import ConfirmDeleteAction from '@/components/designSystem/modals/confirm.delete.action.vue'
import Spinner from '@/components/others/Spinner.vue'
import EditIcon from '@/assets/icons/edit.vue'
import { usePolicyLinkEditPermission } from '@/hooks/usePolicyLinkPermissions'
import { AuthorizationResourcesAndSubjects } from '@/types/authorization'

import DsPolicySelect from '../Select/DsPolicySelect.vue'
import type { PolicyLinksAdapter } from './types'

interface DraftItem {
  policyId: number
  policyName: string
  startDate: string
  endDate: string
  linkedAt: string
  linkedBy: string
  status: 'vinculada' | 'em_edicao' | 'nao_vinculada'
}

type PolicyLinkDomainItem = Pick<
  CompanyDomainItem,
  'startDate' | 'endDate' | 'dsPolicy' | 'createdAt' | 'linkedBy'
>

interface SelectedDsPolicy {
  id: number
  name: string
  createdBy?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    resourceType: {},
    resourceId: {},
    companyId: {},
    timezone: {},
    adapter: {},
    bypassEditPermission: { type: Boolean }
  },
  emits: ["update:isSaving", "update:hasUnsavedChanges", "saved"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;





const toast = useToast()
const { notifyError } = useErrorToast()

const policyService = new PolicyService()

const draftItems = ref<DraftItem[]>([])
const showAddPolicyForm = ref(false)
const selectedPolicy = ref<SelectedDsPolicy | undefined>(undefined)
const startDate = ref<string | Date>('')
const endDate = ref<string | Date>('')
const showEditPolicyForm = ref(false)
const editItemIndex = ref<number | null>(null)
const editPolicy = ref<DraftItem | null>(null)
const originalItems = ref<DraftItem[]>([])
const isLoadingPolicies = ref(false)
const isSavingPolicies = ref(false)
const showEmptyListConfirm = ref(false)

const { canEditPolicyLinks } = usePolicyLinkEditPermission(() => props.resourceType)

const canEdit = computed(() => props.bypassEditPermission || canEditPolicyLinks.value)

const tableColumns = computed(() => [
  {
    key: 'name',
    label: props.adapter?.policyColumnLabel ?? 'Política de restrição',
    cols: 'lg:col-span-3 md:col-span-4 col-span-12'
  },
  { key: 'period', label: 'Período', cols: 'lg:col-span-3 md:col-span-4 col-span-12' },
  {
    key: 'linkedAt',
    label: 'Data do vínculo',
    cols: 'lg:col-span-1 md:col-span-3 col-span-12'
  },
  {
    key: 'responsible',
    label: 'Responsável',
    cols: 'lg:col-span-2 md:col-span-4 col-span-12'
  }
])

const fetchLinkedPolicies = async () => {
  isLoadingPolicies.value = true
  try {
    if (props.adapter) {
      const items = await props.adapter.listLinks(props.resourceType, props.resourceId)
      draftItems.value = items.map((item) => ({ ...item, status: 'vinculada' as const }))
      originalItems.value = JSON.parse(JSON.stringify(draftItems.value))
      return
    }

    let response:
      | GetCompanyDomainResponse
      | GetHealthUnitDomainResponse
      | GetScheduleDomainResponse
      | GetPositionDomainResponse
      | undefined
    const { resourceType, resourceId, timezone } = props

    switch (resourceType) {
      case AuthorizationResourcesAndSubjects.Company:
        response = await policyService.listCompanyDsPolicyLinks(resourceId)
        break
      case AuthorizationResourcesAndSubjects.HealthUnit:
        response = await policyService.listHealthUnitDsPolicyLinks(resourceId)
        break
      case AuthorizationResourcesAndSubjects.Schedule:
        response = await policyService.listScheduleDsPolicyLinks(resourceId)
        break
      case AuthorizationResourcesAndSubjects.Position:
        response = await policyService.listPositionDsPolicyLinks(resourceId)
        break
    }

    const items: PolicyLinkDomainItem[] = response?.data ?? []
    draftItems.value = items.map((item) => {
      const start = item.startDate
      const end = item.endDate

      const isSchedule = resourceType === AuthorizationResourcesAndSubjects.Schedule

      const adjustedStart = isSchedule && timezone ? applyTimezoneOffset(start, timezone) : start

      const adjustedEnd = isSchedule && timezone ? applyTimezoneOffset(end, timezone, true) : end

      return {
        policyId: item.dsPolicy?.id,
        policyName: item.dsPolicy?.name,
        startDate: adjustedStart,
        endDate: adjustedEnd,
        linkedAt: item.createdAt,
        linkedBy: item.linkedBy?.name ?? '',
        status: 'vinculada'
      }
    })

    originalItems.value = JSON.parse(JSON.stringify(draftItems.value))
  } catch (error: unknown) {
    notifyError(error, 'Não foi possível carregar as políticas vinculadas.')
  } finally {
    isLoadingPolicies.value = false
  }
}

onMounted(() => {
  fetchLinkedPolicies()
})

const applyTimezoneOffset = (dateStr: string, offset: string, isEnd = false): string => {
  const date = new Date(dateStr)

  if (isEnd) {
    date.setUTCDate(date.getUTCDate() - 1)
    date.setUTCHours(23, 59, 59, 999)
  } else {
    date.setUTCHours(0, 0, 0, 0)
  }

  const [sign, hours, minutes] = offset.match(/([+-])(\d{2}):(\d{2})/)!.slice(1)
  const totalOffset = (parseInt(hours) * 60 + parseInt(minutes)) * (sign === '+' ? -1 : 1)
  const adjustedDate = new Date(date.getTime() + totalOffset * 60 * 1000)
  return adjustedDate.toISOString()
}

const hasChanges = computed(() => {
  return JSON.stringify(draftItems.value) !== JSON.stringify(originalItems.value)
})

watch(hasChanges, (value) => {
  emit('update:hasUnsavedChanges', value)
})

watch(isSavingPolicies, (value) => {
  emit('update:isSaving', value)
})

const handleRevertChanges = () => {
  draftItems.value = JSON.parse(JSON.stringify(originalItems.value))
}

const handleCancelAddPolicy = () => {
  showAddPolicyForm.value = false
  selectedPolicy.value = undefined
  startDate.value = ''
  endDate.value = ''
}

const formatDate = (date: string | Date, isStart = true): string => {
  if (typeof date === 'string') date = new Date(date)
  const adjusted = new Date(date)
  adjusted.setUTCHours(isStart ? 0 : 23, isStart ? 0 : 59, isStart ? 0 : 59, isStart ? 0 : 999)
  return adjusted.toISOString()
}

const handleAddPolicy = () => {
  if (!selectedPolicy.value || !startDate.value || !endDate.value) {
    toast.error('Preencha todos os campos para incluir a política.')
    return
  }

  draftItems.value.push({
    policyId: selectedPolicy.value.id,
    policyName: selectedPolicy.value.name,
    startDate: formatDate(startDate.value, true),
    endDate: formatDate(endDate.value, false),
    linkedAt: new Date().toISOString(),
    linkedBy: selectedPolicy.value.createdBy ?? 'Usuário atual',
    status: 'nao_vinculada'
  })

  handleCancelAddPolicy()
}

const handleSave = async () => {
  const invalidItems = draftItems.value.filter(
    (item) => !item.policyId || !item.startDate || !item.endDate
  )

  if (invalidItems.length > 0) {
    // Vínculo com vigência aberta (endDate null vindo do backend) exige data final
    const onlyOpenEnded = invalidItems.every(
      (item) => item.policyId && item.startDate && !item.endDate
    )
    toast.error(
      onlyOpenEnded
        ? 'Há vínculo com vigência aberta (sem data final). Preencha a data final para salvar.'
        : 'Todos os campos devem estar preenchidos.'
    )
    return
  }

  // O PATCH substitui a timeline inteira: lista vazia apaga todos os vínculos
  if (draftItems.value.length === 0 && originalItems.value.length > 0) {
    showEmptyListConfirm.value = true
    return
  }

  await performSave()
}

const handleConfirmEmptySave = async () => {
  showEmptyListConfirm.value = false
  await performSave()
}

const performSave = async () => {
  isSavingPolicies.value = true
  try {
    const { resourceType, resourceId } = props

    if (props.adapter) {
      await props.adapter.saveLinks(
        resourceType,
        resourceId,
        draftItems.value.map((item) => ({
          policyId: Number(item.policyId),
          startDate: formatDate(item.startDate, true),
          endDate: formatDate(item.endDate, false)
        }))
      )

      toast.success('Políticas salvas com sucesso!')
      await fetchLinkedPolicies()
      emit('saved')
      return
    }

    const formattedItems = draftItems.value.map((item) => ({
      dsPolicyId: Number(item.policyId),
      startDate: formatDate(item.startDate, true),
      endDate: formatDate(item.endDate, false)
    }))

    switch (resourceType) {
      case AuthorizationResourcesAndSubjects.Company:
        await policyService.updateCompanyDsPolicyLinks(resourceId, {
          policies: formattedItems
        })
        break
      case AuthorizationResourcesAndSubjects.HealthUnit:
        await policyService.updateHealthUnitDsPolicyLinks(resourceId, {
          policies: formattedItems
        })
        break
      case AuthorizationResourcesAndSubjects.Schedule:
        await policyService.updateScheduleDsPolicyLinks(resourceId, {
          policies: formattedItems
        })
        break
      case AuthorizationResourcesAndSubjects.Position:
        await policyService.updatePositionDsPolicyLinks(resourceId, {
          policies: formattedItems
        })
        break
      default:
        toast.error('Tipo de recurso não reconhecido.')
        return
    }

    toast.success('Políticas salvas com sucesso!')
    await fetchLinkedPolicies()
    emit('saved')
  } catch (error: unknown) {
    notifyError(error, 'Não foi possível salvar as políticas vinculadas.')
  } finally {
    isSavingPolicies.value = false
  }
}

const handleEdit = (index: number) => {
  editItemIndex.value = index
  editPolicy.value = { ...draftItems.value[index] }
  showEditPolicyForm.value = true
}

const handleCancelEditPolicy = () => {
  showEditPolicyForm.value = false
  editItemIndex.value = null
  editPolicy.value = null
}

const handleConfirmEditPolicy = () => {
  if (!editPolicy.value) {
    toast.error('Algo deu errado. Dados da edição não encontrados.')
    return
  }

  if (!editPolicy.value.startDate || !editPolicy.value.endDate) {
    toast.error('Preencha todos os campos.')
    return
  }

  editPolicy.value.startDate = formatDate(editPolicy.value.startDate, true)
  editPolicy.value.endDate = formatDate(editPolicy.value.endDate, false)

  if (editItemIndex.value !== null) {
    draftItems.value[editItemIndex.value] = {
      ...editPolicy.value,
      status: 'em_edicao'
    }

    handleCancelEditPolicy()
  }
}

const handleDeletePolicy = () => {
  if (editItemIndex.value !== null) {
    draftItems.value.splice(editItemIndex.value, 1)
    handleCancelEditPolicy()
  }
}

const formatToBR = (isoDate: string) => {
  if (!isoDate) return '-'

  const date = new Date(isoDate)
  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = date.getUTCFullYear()

  return `${day}/${month}/${year}`
}

const policies = computed(() =>
  draftItems.value.map((item) => ({
    id: item.policyId,
    name: item.policyName,
    period:
      item.startDate && item.endDate
        ? `${formatToBR(item.startDate)} à ${formatToBR(item.endDate)}`
        : '-',
    linkedAt: item.linkedAt ? formatToBR(item.linkedAt) : '-',
    responsible: item.linkedBy ?? '-',
    status: item.status
  }))
)

__expose({ handleSave, handleRevertChanges })

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!

  return (_openBlock(), _createElementBlock("div", null, [
    (isLoadingPolicies.value || isSavingPolicies.value)
      ? (_openBlock(), _createBlock(Spinner, {
          key: 0,
          color: "primary",
          size: "5rem",
          width: "0.5rem",
          class: "absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
        }))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      _hoisted_2,
      _createElementVNode("div", _hoisted_3, [
        (hasChanges.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              "data-testid": "revert-policy-changes-button",
              class: "text-sm text-gray-400 hover:text-gray-700",
              onClick: handleRevertChanges
            }, " Reverter alterações "))
          : _createCommentVNode("", true),
        _createVNode(OutlineButton, {
          "data-testid": "add-policy-button",
          text: "Adicionar política",
          height: "32px",
          "append-icon": _unref(mdiPlus),
          class: "text-sm !w-auto !px-4 !py-1",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (showAddPolicyForm.value = !showAddPolicyForm.value)),
          disabled: !canEdit.value
        }, null, 8, ["append-icon", "disabled"]),
        (showAddPolicyForm.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", { class: "flex justify-between items-center mb-3" }, [
                _hoisted_5,
                _createElementVNode("button", {
                  "data-testid": "cancel-add-policy-button",
                  class: "text-gray-400 hover:text-gray-700",
                  onClick: handleCancelAddPolicy
                }, " ✕ ")
              ]),
              _createVNode(DsPolicySelect, {
                modelValue: selectedPolicy.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedPolicy).value = $event)),
                "data-testid": "add-policy-select-input",
                "company-id": props.companyId,
                "fetch-policies": props.adapter?.fetchPolicyOptions,
                class: "w-full mb-4"
              }, null, 8, ["modelValue", "company-id", "fetch-policies"]),
              _createVNode(_unref(DatePicker), {
                modelValue: startDate.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((startDate).value = $event)),
                locale: "pt-BR",
                masks: { modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' },
                "is-required": ""
              }, {
                default: _withCtx(({ inputValue, inputEvents }) => [
                  _createVNode(_component_v_text_field, _mergeProps({
                    label: "Início da vigência",
                    "data-testid": "add-policy-start-date-input",
                    "model-value": inputValue
                  }, _toHandlers(inputEvents), {
                    class: "w-full",
                    color: "primary20",
                    variant: "outlined",
                    density: "comfortable",
                    "prepend-inner-icon": _unref(mdiCalendar)
                  }), null, 16, ["model-value", "prepend-inner-icon"])
                ]),
                _: 1
              }, 8, ["modelValue"]),
              _createVNode(_unref(DatePicker), {
                modelValue: endDate.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((endDate).value = $event)),
                locale: "pt-BR",
                masks: { modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' },
                "is-required": ""
              }, {
                default: _withCtx(({ inputValue, inputEvents }) => [
                  _createVNode(_component_v_text_field, _mergeProps({
                    label: "Final da vigência",
                    "data-testid": "add-policy-end-date-input",
                    "model-value": inputValue
                  }, _toHandlers(inputEvents), {
                    class: "w-full mb-4",
                    color: "primary20",
                    variant: "outlined",
                    density: "comfortable",
                    "prepend-inner-icon": _unref(mdiCalendar)
                  }), null, 16, ["model-value", "prepend-inner-icon"])
                ]),
                _: 1
              }, 8, ["modelValue"]),
              _createVNode(FilledButton, {
                "data-testid": "confirm-add-policy-button",
                text: "Incluir política",
                width: "100%",
                onClick: handleAddPolicy
              })
            ]))
          : _createCommentVNode("", true),
        (showEditPolicyForm.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("div", { class: "flex justify-between items-center mb-3" }, [
                _hoisted_7,
                _createElementVNode("button", {
                  "data-testid": "cancel-edit-policy-button",
                  class: "text-gray-400 hover:text-gray-700",
                  onClick: handleCancelEditPolicy
                }, " ✕ ")
              ]),
              _createElementVNode("div", _hoisted_8, " Data de criação: " + _toDisplayString(formatToBR(editPolicy.value?.linkedAt || '')), 1),
              _createVNode(DsPolicySelect, {
                modelValue: editPolicy.value!.policyName,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((editPolicy.value!.policyName) = $event)),
                "fetch-policies": props.adapter?.fetchPolicyOptions,
                class: "w-full mb-4",
                disabled: ""
              }, null, 8, ["modelValue", "fetch-policies"]),
              _createVNode(_unref(DatePicker), {
                modelValue: editPolicy.value!.startDate,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((editPolicy.value!.startDate) = $event)),
                locale: "pt-BR",
                masks: { modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' },
                "is-required": ""
              }, {
                default: _withCtx(({ inputValue, inputEvents }) => [
                  _createVNode(_component_v_text_field, _mergeProps({
                    label: "Início da vigência",
                    "data-testid": "edit-policy-start-date-input",
                    "model-value": inputValue
                  }, _toHandlers(inputEvents), {
                    class: "w-full",
                    color: "primary20",
                    variant: "outlined",
                    density: "comfortable",
                    "prepend-inner-icon": _unref(mdiCalendar)
                  }), null, 16, ["model-value", "prepend-inner-icon"])
                ]),
                _: 1
              }, 8, ["modelValue"]),
              _createVNode(_unref(DatePicker), {
                modelValue: editPolicy.value!.endDate,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((editPolicy.value!.endDate) = $event)),
                locale: "pt-BR",
                masks: { modelValue: 'YYYY-MM-DD', input: 'DD/MM/YYYY' },
                "is-required": ""
              }, {
                default: _withCtx(({ inputValue, inputEvents }) => [
                  _createVNode(_component_v_text_field, _mergeProps({
                    label: "Final da vigência",
                    "data-testid": "edit-policy-end-date-input",
                    "model-value": inputValue
                  }, _toHandlers(inputEvents), {
                    class: "w-full mb-4",
                    color: "primary20",
                    variant: "outlined",
                    density: "comfortable",
                    "prepend-inner-icon": _unref(mdiCalendar)
                  }), null, 16, ["model-value", "prepend-inner-icon"])
                ]),
                _: 1
              }, 8, ["modelValue"]),
              _createElementVNode("div", _hoisted_9, [
                _createVNode(FilledButton, {
                  "data-testid": "confirm-edit-policy-button",
                  text: "Concluir",
                  width: "50%",
                  onClick: handleConfirmEditPolicy
                }),
                _createVNode(FilledButton, {
                  "data-testid": "delete-policy-link-button",
                  text: "Excluir vínculo",
                  width: "50%",
                  class: "!bg-transparent border-2 !border-red-500 !text-red-500",
                  onClick: handleDeletePolicy
                })
              ])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    (policies.value.length)
      ? (_openBlock(), _createBlock(TableModular, {
          key: 1,
          rows: policies.value,
          columns: tableColumns.value
        }, {
          actions: _withCtx(({ index }) => [
            (canEdit.value)
              ? (_openBlock(), _createBlock(EditIcon, {
                  key: 0,
                  "data-testid": "edit-policy-link-button",
                  color: "#2f7f91",
                  class: "cursor-pointer ml-4 w-5 h-5",
                  onClick: ($event: any) => (handleEdit(index))
                }, null, 8, ["onClick"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["rows", "columns"]))
      : (_openBlock(), _createBlock(EmptyList, {
          key: 2,
          title: "Nenhuma política vinculada encontrada",
          class: "mt-8"
        })),
    _createVNode(ConfirmDeleteAction, {
      "data-testid": "confirm-empty-save-dialog",
      show: showEmptyListConfirm.value,
      title: "Remover todos os vínculos?",
      description: "Você removeu todas as políticas da lista. Ao salvar, todos os vínculos de política deste recurso serão excluídos permanentemente. Deseja continuar?",
      "text-button": "Excluir vínculos",
      onCloseModal: _cache[7] || (_cache[7] = ($event: any) => (showEmptyListConfirm.value = false)),
      onHandleConfirm: handleConfirmEmptySave
    }, null, 8, ["show"])
  ]))
}
}

})